import React, { useMemo } from "react";
import { Checkbox, ColorBoxWithLabel } from "../Checkbox";
import { FilterMetadata } from "./types";

import { arrayToMap, SEPARATOR } from "../misc";
import { LoadingIndicator } from "../LoadingIndicator";
import _ from "lodash";
import { formatNumber } from "../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGreaterThanEqual } from "@fortawesome/pro-regular-svg-icons/faGreaterThanEqual";
import { faLessThan } from "@fortawesome/pro-regular-svg-icons/faLessThan";

import styles from "./SearchFilter.module.scss";

export interface SearchFilterProp extends FilterMetadata {
    loading?: boolean;
    checkedValues: string[];
    className?: string;
    category: string;
    onChange?: (category: string, option: string, checked: boolean) => void;
}

const LSearchFilter = ({
    label,
    loading = false,
    options,
    className,
    category,
    checkedValues = [],
    onChange
}: SearchFilterProp) => {
    const checkValueMap = arrayToMap(checkedValues);

    const onValueChange = useMemo(() => {
        return (option: string, checked: boolean) => {
            onChange(category, option, checked);
        };
    }, [category, onChange]);

    return (
        <div className={`${className ?? ""} ${styles.searchFilter}`}>
            <h4>{label}</h4>
            {loading && <LoadingIndicator />}
            {(options ?? []).map(
                ({ id, amount, label, value, queryRangeStart, queryRangeEnd, filteredAmount, disabled }) => {
                    return (
                        <Checkbox
                            key={id}
                            checked={checkValueMap?.[value] === 1}
                            onValueChange={onValueChange}
                            className={`${styles.searchFilterCheckbox} ${
                                disabled ? styles.filteredOptionDisabled : ""
                            }`}
                            value={value}
                        >
                            {label}{" "}
                            {queryRangeStart != null ? (
                                <span>
                                    (
                                    {queryRangeEnd == null ? (
                                        <span>
                                            <FontAwesomeIcon icon={faGreaterThanEqual} className={styles.mathSign} />{" "}
                                            {`${formatNumber(queryRangeStart)}`}
                                        </span>
                                    ) : (
                                        `${formatNumber(queryRangeStart)} - ${formatNumber(queryRangeEnd)}`
                                    )}
                                    )
                                </span>
                            ) : (
                                ""
                            )}
                            {queryRangeStart == null && queryRangeEnd != null ? (
                                <span>
                                    (<FontAwesomeIcon icon={faLessThan} className={styles.mathSign} />{" "}
                                    {`${formatNumber(queryRangeEnd)}`})
                                </span>
                            ) : (
                                ""
                            )}{" "}
                            -{" "}
                            {`${
                                filteredAmount == null
                                    ? //|| filteredAmount === amount
                                      formatNumber(amount)
                                    : formatNumber(filteredAmount)
                            }`}
                        </Checkbox>
                    );
                }
            )}
        </div>
    );
};

export const SearchFilter = React.memo(LSearchFilter, (prev, next) => {
    return (
        prev.category === next.category &&
        _.join(prev?.checkedValues ?? [], SEPARATOR) === _.join(next?.checkedValues ?? [], SEPARATOR) &&
        prev.className === next.className &&
        prev.onChange === next.onChange &&
        prev.loading === next.loading &&
        prev.label === next.label &&
        // prev.options === next.options &&
        _.join(
            prev.options.map(d => d.id ?? ""),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.id ?? ""),
                SEPARATOR
            ) &&
        _.join(
            prev.options.map(d => d.amount ?? 0),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.amount ?? 0),
                SEPARATOR
            ) &&
        _.join(
            prev.options.map(d => d.filteredAmount ?? 0),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.filteredAmount ?? 0),
                SEPARATOR
            ) &&
        _.join(
            prev.options.map(d => d.disabled ?? false),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.disabled ?? false),
                SEPARATOR
            )
    );
});

export interface SearchFilterStyleLegendProp extends FilterMetadata {
    loading?: boolean;
    className?: string;
}

const LSearchFilterStyleLegend = ({ label, loading = false, options, className }: SearchFilterStyleLegendProp) => {
    return (
        <div className={`${className ?? ""} ${styles.searchFilter}`}>
            <h4>{label}</h4>
            {loading && <LoadingIndicator />}
            {(options ?? []).map(({ id, label, color, amount, disabled, filteredAmount }) => {
                return (
                    <ColorBoxWithLabel
                        key={id}
                        color={color}
                        className={`${styles.searchFilterLegend} ${disabled ? styles.filteredOptionDisabled : ""}`}
                    >
                        {label} - {formatNumber(filteredAmount ?? amount)}
                    </ColorBoxWithLabel>
                );
            })}
        </div>
    );
};

export const SearchFilterStyleLegend = React.memo(LSearchFilterStyleLegend, (prev, next) => {
    return (
        prev.className === next.className &&
        prev.label === next.label &&
        _.join(
            prev.options.map(d => d.id ?? ""),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.id ?? ""),
                SEPARATOR
            ) &&
        _.join(
            prev.options.map(d => d.amount ?? 0),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.amount ?? 0),
                SEPARATOR
            ) &&
        _.join(
            prev.options.map(d => d.filteredAmount ?? 0),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.filteredAmount ?? 0),
                SEPARATOR
            ) &&
        _.join(
            prev.options.map(d => d.disabled ?? false),
            SEPARATOR
        ) ===
            _.join(
                next.options.map(d => d.disabled ?? false),
                SEPARATOR
            ) &&
        prev.loading === next.loading
    );
});
